import { defineStore } from "pinia";

import { AdminApiError } from "../../api-client.js";
import { createAdminAuthenticationService, type AdminAuthenticationService } from "./service.js";
import type { AdminSessionContext, AdminSessionStatus } from "./models.js";

export const useAdminSessionStore = defineStore("admin-session", {
  state: (): {
    context?: AdminSessionContext;
    errorMessage?: string;
    service: AdminAuthenticationService;
    status: AdminSessionStatus;
  } => ({
    service: createAdminAuthenticationService(),
    status: "unknown",
  }),
  getters: {
    permissions: (state): string[] => state.context?.user.permissions ?? [],
    csrfToken: (state): string | undefined => state.context?.csrfToken,
    role: (state): string | undefined => state.context?.user.role,
    sessionView: (state) =>
      state.context === undefined
        ? null
        : {
            adminUserId: state.context.user.adminUserId,
            email: state.context.user.email,
            role: state.context.user.role,
            expiresAt: state.context.expiresAt,
            permissions: state.context.user.permissions,
          },
  },
  actions: {
    configure(service: AdminAuthenticationService): void {
      this.service = service;
    },
    async bootstrap(options: { force?: boolean } = {}): Promise<void> {
      writeAdminAuthDebugLog("bootstrap:start", {
        force: options.force === true,
        status: this.status,
      });
      if (this.status !== "unknown" && options.force !== true) {
        writeAdminAuthDebugLog("bootstrap:skip", { status: this.status });
        return;
      }
      try {
        this.context = await this.service.loadSession();
        this.status = "authenticated";
        writeAdminAuthDebugLog("bootstrap:success", { status: this.status });
      } catch (error: unknown) {
        this.applyBootstrapError(error);
        writeAdminAuthDebugLog("bootstrap:error", {
          status: this.status,
          kind: error instanceof AdminApiError ? error.kind : "unknown",
        });
      }
    },
    async logout(): Promise<void> {
      await this.service.logout();
      this.status = "unauthenticated";
      delete this.context;
      globalThis.location.assign("/#/login");
    },
    applyBootstrapError(error: unknown): void {
      delete this.context;
      if (error instanceof AdminApiError && error.kind === "auth") {
        this.status = "expired";
        this.errorMessage = error.message;
        return;
      }

      if (error instanceof AdminApiError && error.kind === "access") {
        this.status = "revoked";
        this.errorMessage = error.message;
        return;
      }

      this.status = "unauthenticated";
      this.errorMessage =
        error instanceof Error ? error.message : "Admin session could not be loaded.";
    },
  },
});

function writeAdminAuthDebugLog(event: string, details: Record<string, unknown>): void {
  if (import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "dev") {
    void event;
    void details;
  }
}
